import { Dialog } from '@headlessui/react';
import { type ReactNode, useEffect, useState } from 'react';

const DESKTOP_QUERY = '(min-width: 1360px)';

type Props = {
  /** How many filters are applied; shown on the button as "Filters (N)". */
  count: number;
  children: ReactNode;
  /** Controlled open state, for a page that closes the drawer when a search runs. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * [MCV-1889] DD22: from 1360px up the filters render where they always did. Below it they collapse
 * into a "Filters (N)" button that opens them as a full-screen drawer, so a single-pane list keeps
 * its room. The filters mount in one place at a time and remount on each toggle, so what they track
 * has to live in the page: the form state does, and so does a filter's own (see useOfferingPrefill).
 */
export const ResponsiveFilters = ({ count, children, open, onOpenChange }: Props) => {
  const [innerOpen, setInnerOpen] = useState(false);
  const isOpen = open ?? innerOpen;
  const setOpen = (next: boolean) => {
    setInnerOpen(next);
    onOpenChange?.(next);
  };

  // A drawer left open while the window is widened would end up as a stray block under the page.
  useEffect(() => {
    if (!isOpen || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(DESKTOP_QUERY);
    const close = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setInnerOpen(false);
        onOpenChange?.(false);
      }
    };
    media.addEventListener('change', close);
    return () => media.removeEventListener('change', close);
  }, [isOpen, onOpenChange]);

  return (
    <>
      {/* #root centers text, so the button needs a left-aligned parent. */}
      <div className="text-left min-[1360px]:hidden">
        <button
          type="button"
          data-cy="filters-drawer-button"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          className="mb-3 inline-flex min-h-10 items-center rounded-md border border-grey-800 bg-white px-3.5 py-2 text-sm font-semibold text-content-primary"
          onClick={() => setOpen(true)}
        >
          Filters ({count})
        </button>
      </div>
      {isOpen ? (
        // The shared Headless UI dialog moves focus in, traps it, closes on Escape and returns focus
        // to the Filters button.
        <Dialog
          open
          onClose={() => setOpen(false)}
          aria-label={`Filters (${count})`}
          data-cy="filters-drawer"
          className="fixed inset-0 z-40 overflow-y-auto bg-base p-4 min-[1360px]:hidden"
        >
          <div className="mb-3 flex items-center justify-between">
            <h2 className="sub-heading-sb">Filters ({count})</h2>
            <button
              type="button"
              data-cy="filters-drawer-done"
              className="rounded-md px-3 py-1.5 text-sm font-semibold text-primary-50 hover:bg-primary-t-900"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>
          {children}
        </Dialog>
      ) : (
        <div className="max-[1359px]:hidden">{children}</div>
      )}
    </>
  );
};
